import find from 'lodash/find';
import filter from 'lodash/filter';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import forEach from 'lodash/forEach';
import entries from 'lodash/entries';
import keyBy from 'lodash/keyBy';

import {
  IncidentTriggerConditionByType,
  IncidentTriggerChangeByTriggerType,
  IncidentTriggerBuiltinTypeByTriggerType,
  INCIDENT_DETAILS_PREFIX,
} from 'xm-constants/incidentTriggers';
import {
  builtInTypes,
  incidentTriggerBuiltInTypes,
  scriptStepTypes,
} from '@xmatters/common/constants/scriptSteps';
import { sortTriggers } from 'utils/incidents';
import { getDefaultFlowTriggerIntegrationData } from 'utils/flow/flow';
import { triggerTypeIconPaths } from 'xm-constants/triggerTypes';
import { ALERT_INTEGRATION_DATA_PROP } from 'xm-constants/toolStep';

import {
  getIdealIntegrationName,
  IDEAL_NAME_KEY,
  syncSubTriggerBlocksByMutatingCanvas,
} from './subTriggerUtils';

// FIXME: the definition for Initiate Incident block only needs to be searched once then memoized,
// not once for every block on canvas: https://xmatters.atlassian.net/browse/CIMP-3789
export function isInitiateIncidentBlock(block, customSteps) {
  if (!block?.step) {
    return false;
  }
  const { id, type } = block.step;
  if (type === builtInTypes.XMATTERS_CREATE_INCIDENT) {
    // Freshly dragged from the sidebar
    return true;
  }

  // returned from server:
  if (!customSteps) {
    return false;
  }
  const stepDef = isArray(customSteps)
    ? find(customSteps, s => s.id === id)
    : customSteps[id];
  return stepDef?.builtInType === builtInTypes.XMATTERS_CREATE_INCIDENT;
}

export function getInitiateIncidentTriggerStep(customSteps, stepBuiltInType) {
  return find(
    customSteps,
    ({ builtInType, scriptStepType }) =>
      builtInType === stepBuiltInType && scriptStepType === 'BUILT_IN_INTERNAL',
  );
}

/**
 * When we pull Initiate Incident block from the server, its step.type is SCRIPT_STEP.
 * However, we need to change that to XMATTERS_CREATE_INCIDENT so it can use the
 * InitiateIncident block on canvas. This is only done in the mapStateToProps stage;
 * the original data in Redux store is not modified. Therefore, any other logic
 * that depends on InitiateIncident step.type === SCRIPT_STEP is not affected.
 *
 * TODO: More than 1 place in the repo does this "if InitiateIncident, do something else" logic,
 * and it's getting unwieldy; maybe talk to server devs to see whether it's
 * possible to change step.type to XMATTERS_INITIATE_INCIDENT? Wonder what other
 * logic will that affect in server and client?
 *
 * @param {Object} planCanvas - the state.flowDesigner.planCanvas state slice
 * @param {Object[]} customSteps - state.flowDesigner.customSteps
 * @returns {Object} - planCanvas slice, with its blocks array changed.
 */
export function replaceInitiateIncidentBlock(planCanvas, customSteps) {
  const originalBlocks = planCanvas?.blocks;

  if (!originalBlocks) return planCanvas;

  const originalInitiateIncidentBlocks = filter(originalBlocks, b =>
    isInitiateIncidentBlock(b, customSteps),
  );

  if (!originalInitiateIncidentBlocks.length) return planCanvas;

  const modifiedInitiateIncidentBlocks = map(
    originalInitiateIncidentBlocks,
    originalBlock => ({
      ...originalBlock,
      step: {
        ...originalBlock.step,
        // So VisualizationCanvas' this.view can use InitiateIncident block
        // This line is why this function exists
        type: builtInTypes.XMATTERS_CREATE_INCIDENT,
      },
    }),
  );

  return {
    ...planCanvas,
    // The order of the blocks doesn't matter, so simply filter then append.
    blocks: [
      ...filter(
        originalBlocks,
        block => !isInitiateIncidentBlock(block, customSteps),
      ),
      ...modifiedInitiateIncidentBlocks,
    ],
  };
}

/**
 * Give each Incident Change Trigger a position value, based on this order:
 * CRITICAL => ... => MINIMAL => OPEN => ... => REJECTED
 *
 * @param {object} unsortedTriggerMap - Each trigger will have position 0.
 * @returns {object} - Will be almost identical to unsortedTriggerMap, but with `position` filled in.
 */
export function correctTriggerPositions(unsortedTriggerMap) {
  return entries(unsortedTriggerMap)
    .sort((keyValuePair1, keyValuePair2) =>
      sortTriggers(keyValuePair1[0], keyValuePair2[0]),
    )
    .reduce(
      (sortedTriggerMap, keyValuePair, index) => ({
        ...sortedTriggerMap,
        [keyValuePair[0]]: {
          ...keyValuePair[1],
          jsPlumbId: keyValuePair[1]?.triggerBlockId,
          portType: 'pseudoBlockOutput',
          position: index,
        },
      }),
      {},
    );
}

function getNewInitiateIncidentBlockStepData(
  triggerPort,
  block,
  planId,
  prevStep,
  customSteps,
) {
  const { triggerType } = triggerPort;
  const idealName = getIdealIntegrationName(block, triggerPort);
  const builtinType = IncidentTriggerBuiltinTypeByTriggerType[triggerType];
  const triggeredBy = IncidentTriggerChangeByTriggerType[triggerType];
  const integrationData = {
    ...getDefaultFlowTriggerIntegrationData({
      type: scriptStepTypes.OUTBOUND_WEBHOOK,
      planId,
      uniqueName: idealName,
      id: prevStep?.id || triggerPort.data?.integrationId,
    }),
    triggeredBy,
    deployed: !triggerPort.disabled,
    [IDEAL_NAME_KEY]: idealName,
    triggerCondition: IncidentTriggerConditionByType[triggerType],
    scriptStep: getInitiateIncidentTriggerStep(customSteps, builtinType)?.uuid,
  };

  return {
    id: integrationData.id,
    icon: triggerTypeIconPaths[triggeredBy],
    type: integrationData.type,
    triggeredBy,
    [ALERT_INTEGRATION_DATA_PROP]: integrationData,
  };
}

export function injectInitiateIncidentTriggers(
  selectedCanvas,
  planId,
  customSteps,
  integrations,
) {
  const { blocks } = selectedCanvas;
  const initiateIncidentBlocks = blocks.filter(b =>
    isInitiateIncidentBlock(b, customSteps),
  );

  if (
    !getInitiateIncidentTriggerStep(
      customSteps,
      incidentTriggerBuiltInTypes.SEVERITY_CHANGE,
    )
  ) {
    // the ff is not on
    return;
  }

  if (!initiateIncidentBlocks.length) return;

  const triggerIdToBlockIdMap = {};

  const integrationByIds = keyBy(integrations, 'id');

  initiateIncidentBlocks.forEach(block => {
    const { id } = block;

    const toTrigger = subTriggerBlock => {
      const trigger = integrationByIds[subTriggerBlock.step.id];
      return {
        triggerType: trigger?.triggerCondition?.value,
        triggerBlockId: subTriggerBlock.id,
        position: 0,
      };
    };

    let incidentTriggerMap =
      selectedCanvas.incidentTriggers && selectedCanvas.incidentTriggers[id];

    if (!incidentTriggerMap) {
      // get all the subtriggers
      incidentTriggerMap = correctTriggerPositions(
        blocks
          .filter(b => id === b.groupingBlockId)
          .reduce((acc, b) => {
            const t = toTrigger(b);
            acc[t.triggerType] = t;
            return acc;
          }, {}),
      );
    }

    const incidentTriggerList = Object.values(incidentTriggerMap);
    forEach(incidentTriggerList, trigger => {
      triggerIdToBlockIdMap[trigger.triggerBlockId] = id;
    });

    /* eslint-disable no-param-reassign */
    block.ports = incidentTriggerList;
    syncSubTriggerBlocksByMutatingCanvas({
      block,
      customSteps,
      getStepDataFunction: getNewInitiateIncidentBlockStepData,
      planId,
      selectedCanvas,
      triggerPorts: incidentTriggerList,
    });

    // store the incidentTriggers in the canvas
    if (!selectedCanvas.incidentTriggers) {
      selectedCanvas.incidentTriggers = {};
    }
    selectedCanvas.incidentTriggers[id] = incidentTriggerMap;
  });
  /* eslint-enable no-param-reassign */
}

export const isIncidentDetailsOutput = output =>
  output.name.startsWith(INCIDENT_DETAILS_PREFIX);
